"use client";

import {JSX} from "react";

import Link from "next/link";

import SmartImage from "@/shared/ui/smart-image";

import {useMatchMedia} from "@/shared/hooks/dom/use-match-media";
import {Typography} from "@/shared/ui/data-display/typography/typography";

import "./thing-to-do-card.scss";

export type ThingToDoCardImage = {
    url: string;
    alt?: string;
};

export type ThingToDoCardProps = {
    id: string | number;
    title: string;
    description?: string;
    image: ThingToDoCardImage;
    priceRange?: number | null;
    link: string;
    duration?: number | null;
    category?: {
        id: number;
        title: string;
    };
};

export function ThingToDoCard({id, title, description, image, link, category}: ThingToDoCardProps): JSX.Element {
    const isMobile = useMatchMedia({maxWidth: 767});

    const src = image.url;
    const alt = image.alt ?? title;

    const showCategory = isMobile && category;

    return (
        <Link href={link} className="thing-to-do-card__link">
            <article className="thing-to-do-card" data-id={id}>
                <div className="thing-to-do-card__image-wrapper">
                    <div className="thing-to-do-card__image-container">
                        <SmartImage
                            src={src}
                            alt={alt}
                            fill
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 420px"
                            className="thing-to-do-card__image"
                            priority={false}
                        />
                    </div>
                    {showCategory && (
                        <Typography as="div" size={{desktop: 12, mobile: 10}} className="thing-to-do-card__category">
                            {category.title}
                        </Typography>
                    )}
                </div>

                <div className="thing-to-do-card__content">
                    <Typography as="h3" color="title" weight={500} size={{desktop: 14, mobile: 14}} className="thing-to-do-card__title">
                        {title}
                    </Typography>

                    {description ? (
                        <Typography
                            as="p"
                            color="paragraph"
                            weight={400}
                            size={{desktop: 12, mobile: 12}}
                            className="thing-to-do-card__description"
                            innerHTML={description}
                        />
                    ) : null}

                    {isMobile && (
                        <div className="thing-to-do-card__readmore">
                            <Typography as="span" size={{desktop: 12, mobile: 12}} weight={500}>
                                Read More
                            </Typography>
                        </div>
                    )}
                </div>
            </article>
        </Link>
    );
}
